/**
 * callableErrors — pure tests on a failed Firebase callable (no SDK, no Vue), shared by the
 * error mapper (useStandardizedErrors → FUNC_CONNECTION_LOST) and the lost-reply reader
 * (useFirebaseFunctions isLostReplyError), so the two can never read the same error two ways.
 */

/**
 * "No response was ever received": the callable SDK's code `functions/internal` with the BARE
 * message 'internal'. @firebase/functions turns a rejected `fetch` (network down, the
 * connection dropped mid-call, or a CORS preflight refused — e.g. Cloud Run dropping the
 * OPTIONS while cold-starting from zero) into `{ status: 0, json: null }` and throws
 * `FunctionsError('internal', 'internal')`. Our own backend always throws
 * `HttpsError('internal', \`${operation} failed: ...\`)` (functions/src/utils/errorHandler.ts
 * → handleAndLog), so a server-sent internal error never matches.
 *
 * It cannot prove the write did not happen: a request that left the browser before the
 * connection dropped may still run on the server, and the SDK notes that a backend crash
 * severe enough to skip the CORS header looks the same.
 */
export function isNoResponseCallableError(code: unknown, message: unknown): boolean {
  return code === 'functions/internal' && message === 'internal';
}
